import React from "react";

/**
 * Half-circle flow-rate gauge.
 *  - max defaults to 1500 t/h (Richards Bay terminal max design rate)
 *  - target defaults to 1250 t/h — visualised as a red dashed marker on the arc
 *  - colour bands: red 0→target*0.6, amber target*0.6→target, green ≥ target
 *  - shows a "+/-X t/h vs target" pill below the value
 */
export default function FeedingRateGauge({
    value = 0,
    max = 1500,
    target = 1250,
    label = "TONS PER HOUR",
}) {
    const clamped = Math.max(0, Math.min(value, max));
    const pct = clamped / max;
    const r = 90;
    const cx = 120;
    const cy = 110;
    const startX = cx - r;
    const startY = cy;
    const endX = cx + r;
    const endY = cy;

    // needle / value-arc terminus
    const angle = 180 - pct * 180;
    const rad = (angle * Math.PI) / 180;
    const nx = cx + r * Math.cos(rad);
    const ny = cy - r * Math.sin(rad);
    const arcX = nx;
    const arcY = ny;
    // A half-circle gauge never sweeps more than 180° — always use the small arc.
    const largeArc = 0;

    // Colour: green when at/above target, amber 60–100% of target, else red
    const valueColour =
        clamped >= target ? "#00C853" : clamped >= target * 0.6 ? "#FFB300" : "#E3000F";

    // Target marker on arc
    const targetPct = Math.max(0, Math.min(1, target / max));
    const tAngle = 180 - targetPct * 180;
    const tRad = (tAngle * Math.PI) / 180;
    const tInner = r - 14;
    const tOuter = r + 6;
    const t1x = cx + tInner * Math.cos(tRad);
    const t1y = cy - tInner * Math.sin(tRad);
    const t2x = cx + tOuter * Math.cos(tRad);
    const t2y = cy - tOuter * Math.sin(tRad);
    // Position the TARGET label INSIDE the arc to avoid clipping at the SVG edge,
    // and anchor by the half it sits in (left → start, right → end, centre → middle).
    const tLabelR = r - 30;
    const tlx = cx + tLabelR * Math.cos(tRad);
    const tly = cy - tLabelR * Math.sin(tRad) + 4;
    const tAnchor = tlx < cx - 8 ? "start" : tlx > cx + 8 ? "end" : "middle";

    const delta = Math.round(clamped - target);
    const deltaLabel =
        clamped === 0
            ? null
            : delta >= 0
            ? `+${delta.toLocaleString()} t/h above target`
            : `${delta.toLocaleString()} t/h below target`;
    const deltaPositive = delta >= 0;

    return (
        <div className="flex flex-col items-center" data-testid="feeding-rate-gauge">
            <svg viewBox="0 0 240 150" width="100%" style={{ maxWidth: 320 }}>
                {/* background arc */}
                <path
                    d={`M ${startX} ${startY} A ${r} ${r} 0 0 1 ${endX} ${endY}`}
                    stroke="#E2E8F0"
                    strokeWidth="18"
                    fill="none"
                    strokeLinecap="butt"
                />
                {/* value arc */}
                <path
                    d={`M ${startX} ${startY} A ${r} ${r} 0 ${largeArc} 1 ${arcX} ${arcY}`}
                    stroke={valueColour}
                    strokeWidth="18"
                    fill="none"
                    strokeLinecap="butt"
                />
                {/* target marker — short red dashed tick */}
                <line
                    x1={t1x}
                    y1={t1y}
                    x2={t2x}
                    y2={t2y}
                    stroke="#E3000F"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeDasharray="3 2"
                />
                <text
                    x={tlx}
                    y={tly}
                    fontSize="9"
                    fontWeight="700"
                    textAnchor={tAnchor}
                    fill="#E3000F"
                    style={{ letterSpacing: "0.04em" }}
                >
                    TARGET {target.toLocaleString()}
                </text>
                {/* scale ticks at 0 and max — slightly above the baseline so they don't overlap the centre value */}
                <text x={startX - 2} y={startY - 2} fontSize="9" fill="#94A3B8" textAnchor="end">0</text>
                <text x={endX + 2} y={endY - 2} fontSize="9" fill="#94A3B8" textAnchor="start">{max.toLocaleString()}</text>
                {/* center + needle */}
                <circle cx={cx} cy={cy} r="6" fill="#0B1B3D" />
                <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#0B1B3D" strokeWidth="3" strokeLinecap="round" />
            </svg>
            <div className="mt-1 text-center">
                <div
                    className="metric-value tabular"
                    style={{ fontSize: "40px", color: "var(--grindrod-navy)", lineHeight: 1 }}
                    data-testid="feeding-rate-value"
                >
                    {Math.round(clamped).toLocaleString()}
                </div>
                <div className="uppercase-label mt-1">{label}</div>
                {deltaLabel && (
                    <div
                        className={`mt-2 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold tabular ${
                            deltaPositive
                                ? "bg-[var(--grindrod-green-bg)] text-[#00A344]"
                                : "bg-[#FDE7E9] text-[var(--grindrod-red)]"
                        }`}
                        data-testid="flow-rate-delta"
                    >
                        {deltaLabel}
                    </div>
                )}
            </div>
        </div>
    );
}
